// FriendScanModal — iter110.
//
// Fullscreen camera that opens directly when the user taps "Add Friend"
// in the Friends-page bottom bar. On a successful friend-QR decode the
// camera is replaced by a tasteful confirmation card:
//
//   ┌────────────────────────────────┐
//   │           Add <Name>?          │  (Playfair 18px)
//   │           [Avatar 56px]        │
//   │           Name                 │  (DM Sans 16)
//   │           [Tourist/Local pill] │
//   │   ┌──────────┐ ┌──────────┐    │
//   │   │ Cancel   │ │ Add Frnd │    │
//   │   └──────────┘ └──────────┘    │
//   └────────────────────────────────┘
//
// Confirming POSTs /api/qr/scan with `confirm: true` so the existing
// friend-request flow runs unchanged on the backend.
//
// Implementation notes:
//   • Uses Html5Qrcode just like the existing Connect page scanner.
//   • Decoded payload is decoded preview-only first (we recognise the
//     `/join?type=user&id=<uid>` URL pattern produced by `_build_join_url`)
//     so we can fetch the friend's basic profile (display_name, photo,
//     view_preference) BEFORE committing the friend-request — that way
//     the confirmation card is properly populated.
//   • If we can't recognise the payload, we fall back to handing it to
//     `/api/qr/scan` directly (matches the Connect page behaviour).
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ScanLine } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { SHOW_VIEW_TOGGLE } from "../lib/featureFlags";

const REGION_ID = "habano-friend-scan-region";

export default function FriendScanModal({ open, onClose, onAdded }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scanner = useRef(null);
  const lastSent = useRef(0);
  const [error, setError] = useState(null);
  const [decodedFriend, setDecodedFriend] = useState(null); // { user_id, display_name, profile_photo, view_preference, raw_data }
  const [busy, setBusy] = useState(false);

  const stop = async () => {
    if (scanner.current && scanner.current.isScanning) {
      try { await scanner.current.stop(); } catch { /* ignore */ }
    }
  };

  const handleClose = async () => {
    await stop();
    setDecodedFriend(null);
    setError(null);
    onClose?.();
  };

  // Try to recognise a friend join URL. Returns the user_id or null.
  const extractFriendUid = (raw) => {
    if (!raw) return null;
    try {
      const url = new URL(raw);
      const type = url.searchParams.get("type");
      const id = url.searchParams.get("id");
      if (type === "user" && id) return id;
    } catch { /* not a URL */ }
    // habano:u:<uid> legacy fallback
    const legacy = /^habano:u:([\w-]+)$/i.exec(raw.trim());
    if (legacy) return legacy[1];
    return null;
  };

  const onDecode = async (raw) => {
    if (!raw) return;
    const now = Date.now();
    if (now - lastSent.current < 800) return;
    lastSent.current = now;
    const uid = extractFriendUid(raw);
    if (!uid) {
      // Fall back to the generic /api/qr/scan flow (vendors, businesses, etc).
      try {
        const { data } = await api.post("/qr/scan", { raw_data: raw });
        if (data?.ok && data?.type === "friend" && data?.user_id) {
          toast.success(data.message || t("friends_toast_added"));
          await stop();
          handleClose();
          navigate(`/friends/${data.user_id}`);
          return;
        }
        if (data?.message) toast.message(data.message);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      }
      return;
    }
    // Friend QR detected — fetch preview info and show the confirmation card.
    await stop();
    try {
      const { data } = await api.get(`/friends/preview/${uid}`);
      setDecodedFriend({
        user_id: uid,
        display_name: data.display_name || "Friend",
        profile_photo: data.profile_photo || null,
        view_preference: data.view_preference || null,
        raw_data: raw,
      });
    } catch {
      // Couldn't fetch profile — still show the card with a fallback name.
      setDecodedFriend({
        user_id: uid,
        display_name: "Friend",
        profile_photo: null,
        view_preference: null,
        raw_data: raw,
      });
    }
  };

  const confirmAdd = async () => {
    if (!decodedFriend || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/qr/scan", { raw_data: decodedFriend.raw_data });
      if (data?.ok && data?.type === "friend" && data?.user_id) {
        toast.success(t("friends_toast_added"));
        onAdded?.(data.user_id);
        handleClose();
        navigate(`/friends/${data.user_id}`);
        return;
      }
      // Backend already handled the request (e.g. already friends) — surface message
      if (data?.message) toast.message(data.message);
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Kick the scanner whenever the modal opens. Use a microtask delay so
  // the camera region is in the DOM before Html5Qrcode binds to it.
  useEffect(() => {
    if (!open) return undefined;
    setError(null);
    setDecodedFriend(null);
    let cancelled = false;
    (async () => {
      try {
        await new Promise((r) => setTimeout(r, 50));
        if (cancelled) return;
        if (!scanner.current) scanner.current = new Html5Qrcode(REGION_ID);
        await scanner.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decoded) => { onDecode(decoded); },
          () => {},
        );
      } catch (e) {
        if (!cancelled) setError(e?.message || "Could not access camera. Allow camera permission and try again.");
      }
    })();
    return () => {
      cancelled = true;
      stop();
    };
  }, [open]);

  if (!open) return null;

  const sheet = (
    <div
      data-testid="friend-scan-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 220,
        background: "#0B0B0B",
        display: "flex", flexDirection: "column",
      }}
    >
      {/* Header bar */}
      <header
        className="flex items-center justify-between px-4 pt-4 pb-3"
        style={{ background: "#0B0B0B" }}
      >
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 18,
            color: "#F5F1E8",
            lineHeight: 1.25,
          }}
        >
          {t("friends_scan_camera_title")}
        </h2>
        <button
          type="button"
          onClick={handleClose}
          data-testid="friend-scan-close"
          aria-label="Close"
          className="grid place-items-center"
          style={{
            width: 44, height: 44, minWidth: 44, minHeight: 44,
            background: "transparent", border: "none", padding: 0,
          }}
        >
          <span
            aria-hidden="true"
            className="grid place-items-center"
            style={{
              width: 32, height: 32, borderRadius: 9999,
              background: "rgba(20,20,20,0.8)",
              border: "0.5px solid #333", color: "#fff",
            }}
          >
            <X style={{ width: 16, height: 16 }} strokeWidth={1.5} />
          </span>
        </button>
      </header>

      {/* Camera view OR confirmation card */}
      {!decodedFriend ? (
        <section className="mx-5 mt-2 rounded-[20px] overflow-hidden relative aspect-square"
          data-testid="friend-scan-stage" style={{ background: "#000" }}>
          <div id={REGION_ID}
            className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
          <div className="pointer-events-none absolute inset-12 border-2 border-gold rounded-[14px]" />
          {error && (
            <div
              className="absolute bottom-3 left-3 right-3 text-xs text-center"
              data-testid="friend-scan-error"
              style={{
                color: "#F5F1E8",
                background: "rgba(0,0,0,0.7)",
                padding: "8px 12px",
                borderRadius: 8,
              }}
            >
              {error}
            </div>
          )}
        </section>
      ) : (
        <section
          className="mx-5 mt-2 rounded-[20px] p-6 flex flex-col items-center"
          data-testid="friend-scan-confirm"
          style={{
            background: "#171717",
            border: "1px solid rgba(232,184,75,0.3)",
          }}
        >
          <h3
            data-testid="friend-scan-confirm-title"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 18,
              color: "#F5F1E8",
              marginBottom: 16,
              textAlign: "center",
            }}
          >
            {t("friends_scan_add_confirm_title").replace("{name}", decodedFriend.display_name)}
          </h3>
          <ConfirmAvatar name={decodedFriend.display_name} photo={decodedFriend.profile_photo} />
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600,
              color: "#F5F1E8", marginTop: 12, textAlign: "center",
            }}
          >
            {decodedFriend.display_name}
          </div>
          <ConfirmBadge view={decodedFriend.view_preference} t={t} />

          <div style={{ display: "flex", gap: 8, marginTop: 24, width: "100%" }}>
            <button
              type="button"
              onClick={handleClose}
              data-testid="friend-scan-cancel"
              className="inline-flex items-center justify-center transition-colors hover:bg-[rgba(232,184,75,0.06)]"
              style={{
                flex: 1, height: 48,
                background: "transparent", color: "#E8B84B",
                border: "1.5px solid rgba(232,184,75,0.55)",
                borderRadius: 12,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 600,
              }}
            >
              {t("friends_scan_add_confirm_cancel")}
            </button>
            <button
              type="button"
              onClick={confirmAdd}
              disabled={busy}
              data-testid="friend-scan-confirm-add"
              className="inline-flex items-center justify-center transition-opacity hover:opacity-95"
              style={{
                flex: 1, height: 48,
                background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
                color: "#0B0B0B", border: "none", borderRadius: 12,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16, fontWeight: 700,
                opacity: busy ? 0.6 : 1,
              }}
            >
              {t("friends_scan_add_confirm_confirm")}
            </button>
          </div>
        </section>
      )}

      {!decodedFriend && (
        <div
          className="px-5 mt-4 text-center"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            color: "#9A958A",
          }}
        >
          <ScanLine className="w-4 h-4 inline-block mr-1.5 -mt-0.5 text-gold" strokeWidth={1.5} />
          {t("connect_scan_sub") || "Hold steady — we'll pick it up automatically"}
        </div>
      )}
    </div>
  );

  return createPortal(sheet, document.body);
}

function ConfirmAvatar({ name, photo }) {
  if (photo) {
    return (
      <img
        src={photo} alt="" data-testid="friend-scan-confirm-avatar"
        style={{
          width: 56, height: 56, borderRadius: 9999, objectFit: "cover",
          border: "1px solid rgba(232,184,75,0.4)",
        }}
      />
    );
  }
  const initials = (name || "?").split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span
      data-testid="friend-scan-confirm-avatar"
      className="grid place-items-center"
      style={{
        width: 56, height: 56, borderRadius: 9999,
        background: "#0B0B0B", color: "#E8B84B",
        fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
        border: "1px solid rgba(232,184,75,0.4)",
      }}
    >
      {initials}
    </span>
  );
}

function ConfirmBadge({ view, t }) {
  // iter156 — Hidden globally via SHOW_VIEW_TOGGLE. Component + logic
  // preserved so flipping the flag restores the pill.
  if (!SHOW_VIEW_TOGGLE) return null;
  if (view !== "tourist" && view !== "local") return null;
  const label = view === "tourist" ? t("friends_badge_tourist") : t("friends_badge_local");
  return (
    <span
      data-testid={`friend-scan-confirm-badge-${view}`}
      style={{
        marginTop: 6, display: "inline-block",
        padding: "2px 10px", borderRadius: 999,
        background: "rgba(232,184,75,0.08)",
        border: "1px solid rgba(232,184,75,0.4)",
        color: "#E8B84B",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 11, fontWeight: 600, letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
  );
}
